import { createContext, useContext } from "react";
import type { KpiTileRange } from "@playbookai/widget-schemas";

/**
 * Host-provided resolution of ONE KPI Manager KPI, for the widgets that display
 * a KPI they do not own (`kpiTile` today).
 *
 * Deliberately NOT `useKpiCatalog()`, which is the pick-list seam beside it. A
 * catalogue row is a `KpiDef` — a presentation view-model with no measurement
 * date, no applicable target and no way to say "this reader may not see it".
 * Rendering a tile from that shape would mean inventing those three, and the
 * whole point of the binding is that the tile states what KPI Manager states and
 * says so plainly when it cannot.
 *
 * Keeps `@playbookai/ui` data-blind exactly as `entity-resolver.ts` does: the
 * package depends on the SHAPE, never on a fetch. `useKpiSourceResolver()`
 * returns `null` when no provider is mounted, so a tile renders an honest
 * "not available here" state instead of crashing.
 */

/**
 * Why the tile cannot show a number, when it cannot.
 *
 * `forbidden` and `unavailable` are separate on purpose. "You cannot see this"
 * and "this is gone" are different facts about the workspace, and collapsing
 * them tells a reader their access is broken when a KPI was archived, or that a
 * KPI vanished when they simply lost access to it.
 */
export type KpiSourceStatus = "loading" | "ready" | "unavailable" | "forbidden";

/** One reading from the KPI's append-only observation ledger. */
export interface KpiSourceReading {
  /** ISO instant the measure read this value (`observedAt`, not `createdAt`). */
  readonly observedAt: string;
  readonly value: number;
}

/**
 * KPI Manager's OWN word for how much its source can be trusted
 * (`MeasurementStatus`), passed through rather than re-derived. `null` means the
 * KPI has no projection row at all, which is the "nothing recorded yet" case.
 *
 * It matters to the tile because a value is present only while this is
 * `current`: the platform suppresses the projection otherwise, and the tile must
 * then say WHY it has no number instead of implying nobody ever measured.
 */
export type KpiMeasurementTrust =
  | "not_connected"
  | "current"
  | "stale"
  | "unavailable";

export interface KpiSourceView {
  readonly kpiId: string;
  readonly name: string;
  readonly unit: string;
  /** From the KPI's `direction`; `TARGET_RANGE` resolves to neither. */
  readonly higherIsBetter: boolean | null;
  readonly targetRange?: boolean;
  /**
   * The latest TRUSTED value, or null. Null is a real answer and covers two
   * different situations the tile states differently: a KPI with no readings at
   * all, and a KPI whose measurement guardrail suppressed the projection.
   */
  readonly currentValue: number | null;
  /** Stored decimal text for exact scoring, alongside the display/chart number. */
  readonly currentValueExact?: string | null;
  readonly observedAt: string | null;
  /** See {@link KpiMeasurementTrust}. */
  readonly measurementStatus: KpiMeasurementTrust | null;
  /** The reporting period the latest reading falls in, as KPI Manager labels it. */
  readonly periodLabel: string | null;
  /** The open target's value, or null when the KPI has no current target. */
  readonly target: number | null;
  readonly targetExact?: string | null;
  readonly targetDate: string | null;
  /** Readings inside the chosen window, ASCENDING by `observedAt`. */
  readonly readings: readonly KpiSourceReading[];
  readonly archived: boolean;
  /** Where "Open in KPI Manager" goes. The host owns routing. */
  readonly href: string;
  /**
   * The host's in-app navigation to `href`. Without it a plain click on the
   * anchor reloads the whole SPA — on a live canvas, replacing the canvas.
   */
  readonly onNavigate?: ((href: string) => void) | undefined;
}

export interface KpiSourceResult {
  readonly status: KpiSourceStatus;
  /** Present whenever `status` is `ready`. */
  readonly kpi: KpiSourceView | null;
  /** The host's own sentence for a failure: shown under the heading, or as
   * the heading itself for `unavailable` (e.g. the module is turned off). */
  readonly message?: string | undefined;
  readonly onRetry?: (() => void) | undefined;
}

export interface KpiSourceResolver {
  readonly useKpiSource: (
    kpiId: string,
    range: KpiTileRange,
  ) => KpiSourceResult;
}

const Ctx = createContext<KpiSourceResolver | null>(null);

export const KpiSourceResolverProvider = Ctx.Provider;

export function useKpiSourceResolver(): KpiSourceResolver | null {
  return useContext(Ctx);
}
